import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation} from '@angular/core';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { HcModal, HcSort, HcTableDataSource, ModalOptions, ModalService, PaginationComponent, SimpleModalOptions } from '@healthcatalyst/cashmere';
import { TermElement, TerminologyService } from '../../shared/services/terminology.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TermChangeElement, TermChangeService } from '../../shared/services/termChange.service';
import { ChangeElement, ChangeService } from '../../shared/services/change.service';
import { OktaService } from '../../shared/services/okta.service';

/** Admin list of terms to edit */
@Component({
    selector: 'pd-admin-terminology',
    templateUrl: './admin-terminology.component.html',
    styleUrls: ['./admin-terminology.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminTerminologyComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<TermElement> = new HcTableDataSource();
    loading = true;
    loadError = false;
    adminTerminologyDisplayedColumns: string[] = ['name', 'usage', 'example', 'types', 'categories', 'dateAdded', 'actions'];
    adminTerminologyPageSize: number = 10;
    categories = ['Clinical', 'General', 'Health Catalyst', 'Industry', 'Technical'];
    types = ['Abbreviation', 'General usage'];
    filterCategories = ['All','Clinical', 'General', 'Health Catalyst', 'Industry', 'Technical'];
    filterTypes = ['All','Abbreviation', 'General usage'];
    selectedCategoriesControl = new FormControl('All', {nonNullable: true});
    selectedTypesControl = new FormControl('All', {nonNullable: true});
    searchTerm = '';
    private modalRef: HcModal<TemplateRef<any>>;
    termExists = false;
    adminTermControl = new FormGroup({
        id: new FormControl(0, {nonNullable: true}),
        dateAdded: new FormControl(new Date(), {nonNullable: true, validators: [Validators.required]}),
        name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        usage: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        types: new FormControl<string[]>([], {nonNullable: true, validators: [Validators.required]}),
        categories: new FormControl<string[]>([], {nonNullable: true, validators: [Validators.required]}),
        example: new FormControl('', {nonNullable: true})
    });
    addChangeControl = new FormGroup({
        term: new FormControl(0, {nonNullable: true}),
        name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        usage: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        comments: new FormControl('', {nonNullable: true}),
        contributorName: new FormControl<string>('', {nonNullable: true, validators: [Validators.required]}),
        contributorEmail: new FormControl<string>('', {nonNullable: true, validators: [Validators.required, Validators.email]})
    });

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get adminTerminologyListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor ( 
        private terminologyService: TerminologyService,
        private modalService: ModalService,
        private termChangeService: TermChangeService,
        private changeService: ChangeService,
        private oktaService: OktaService
    ) {}

    ngOnInit(): void {

        this.terminologyService.termsUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.dataSource = new HcTableDataSource(list);
                this.dataSource.filterPredicate = (filterData: TermElement, filter: string) => this.usageFilter(filterData, filter);
                if ( this.sort && this.paginator ) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                }
            } else {
                this.loadError = this.terminologyService.loadFailed;
                if ( !this.loadError ) {
                    this.loadTerms();
                } else {
                    this.loading = false;
                }
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        setTimeout(() => {
            this.sort.sort( { disableClear: false, id: 'name', start: 'asc' } );
        });
    }

    applyFilter(searchTerm: string): void {
        const filterStr = searchTerm;
        if (filterStr) {
            this.dataSource.filter = filterStr.trim().toLowerCase();
        } else {
            this.dataSource.filter = ' ';
        }
    }

    usageFilter(data: TermElement, filter: string): boolean {
        const catMatch =
            data.categories.toLowerCase().includes(this.selectedCategoriesControl.value.toLowerCase()) || this.selectedCategoriesControl.value === 'All';
        const typeMatch = data.types.toLowerCase().includes(this.selectedTypesControl.value.toLowerCase()) || this.selectedTypesControl.value === 'All';
        const termMatch = data.name.toLowerCase().includes(filter) || filter === ' ';
        const defMatch = data.usage.toLowerCase().includes(filter) || filter === ' ';

        return catMatch && typeMatch && (termMatch || defMatch);
    }

    loadTerms(): void {
        this.terminologyService.refresh();
    }

    openAddEditModal(content: TemplateRef<any>, edit: boolean, term?: TermElement) {
        const options: ModalOptions = {
            size: 'md',
            data: {
                term: term,
                edit: edit
            }
        };
        this.adminTermControl.reset();
        this.termExists = false;

        if( term ) {
            this.adminTermControl.patchValue({
                id: term.id,
                name: term.name,
                usage: term.usage,
                types: term.types.split(','),
                categories: term.categories.split(','),
                example: term.example,
                dateAdded: term.dateAdded ? term.dateAdded : new Date()
             });
        } 
        this.modalRef = this.modalService.open(content, options);
    }

    editTerm( element: TermElement ) {
        const target: TermElement = {
            id: this.adminTermControl.controls['id'].value,
            name: this.adminTermControl.controls['name'].value,
            usage: this.adminTermControl.controls['usage'].value,
            types: this.adminTermControl.controls['types'].value.toString(),
            categories: this.adminTermControl.controls['categories'].value.toString(),
            example: this.adminTermControl.controls['example'].value,
            dateAdded: this.adminTermControl.controls['dateAdded'].value
        };
        const today = new Date();
        let changeArray: ChangeElement[] = [];
        if ( target.name !== element.name ) {
            const change: ChangeElement = {
                item: target.id,
                name: target.name,
                category: "terminology",
                field: "name",
                previous: element.name,
                update: target.name,
                user: this.oktaService.username.value,
                date: today.toISOString()
            }
            changeArray.push( change );
        }
        if ( target.usage !== element.usage ) {
            const change: ChangeElement = {
                item: target.id,
                name: target.name,
                category: "terminology",
                field: "usage",
                previous: element.usage,
                update: target.usage,
                user: this.oktaService.username.value,
                date: today.toISOString()
            }
            changeArray.push( change );
        }
        if ( target.types !== element.types ) {
            const change: ChangeElement = {
                item: target.id,
                name: target.name,
                category: "terminology",
                field: "types",
                previous: element.types,
                update: target.types,
                user: this.oktaService.username.value,
                date: today.toISOString()
            }
            changeArray.push( change );
        }
        if ( target.categories !== element.categories ) {
            const change: ChangeElement = {
                item: target.id,
                name: target.name,
                category: "terminology",
                field: "categories",
                previous: element.categories,
                update: target.categories,
                user: this.oktaService.username.value,
                date: today.toISOString()
            }
            changeArray.push( change );
        }
        if ( target.example !== element.example ) {
            const change: ChangeElement = {
                item: target.id,
                name: target.name,
                category: "terminology",
                field: "example",
                previous: element.example,
                update: target.example,
                user: this.oktaService.username.value,
                date: today.toISOString()
            }
            changeArray.push( change );
        }
        this.terminologyService.update( target );
        this.changeService.add( changeArray );
        this.modalRef.close();
    }

    addTerm() {
        const termCount = this.terminologyService.termList.value ? this.terminologyService.termList.value.length : 0;
        for ( let i=0; i < termCount; i++ ) {
            if ( this.terminologyService.termList.value && this.adminTermControl.controls['name'].value === this.terminologyService.termList.value[i].name ) {
                this.adminTermControl.controls['name'].setErrors({'unique': false});
                this.termExists = true;
                break;
            }
        }

        if ( !this.termExists ) {
            const today = new Date();
            const target: TermElement = {
                id: -1,
                name: this.adminTermControl.controls['name'].value,
                usage: this.adminTermControl.controls['usage'].value,
                types: this.adminTermControl.controls['types'].value.toString(),
                categories: this.adminTermControl.controls['categories'].value.toString(),
                example: this.adminTermControl.controls['example'].value,
                dateAdded: this.adminTermControl.controls['dateAdded'].value
            };
            const change: ChangeElement = {
                item: -1,
                name: target.name,
                category: "terminology",
                field: target.name,
                previous: "--",
                update: "Term Added",
                user: this.oktaService.username.value,
                date: today.toISOString()
            }
            this.terminologyService.add( target, change );
            this.modalRef.close();
        }
    }

    removeTerm( element: TermElement ) {
        const today = new Date();
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the term: <strong>' + element.name + '</strong>?<p>',
            headerText: 'Delete Term',
            confirmButtonText: 'Delete Term',
            icon: ''
        };
        const change: ChangeElement = {
            item: element.id,
            name: element.name,
            category: "terminology",
            field: element.name,
            previous: "--",
            update: "Term Removed",
            user: this.oktaService.username.value,
            date: today.toISOString()
        };
        this.modalService.confirmDestructive(options)
            .pipe(take(1),filter(confirmed => confirmed))
            .subscribe(() => {
                this.terminologyService.remove( element );
                this.changeService.add( [change] );
            });
    }

    changeRequest(content: TemplateRef<any>, element: TermElement ): void {
        const options: ModalOptions = {
            size: 'md'
        };
        this.addChangeControl.patchValue({
            term: element.id,
            name: element.name,
            usage: element.usage,
            comments: '',
            contributorName: '',
            contributorEmail: ''
         });

        this.modalService.open(content, options);
    }

    submitChangeRequest(): void {
        const target: TermChangeElement = {
            id: null,
            submitDate: new Date(),
            publishDate: null,
            termId: this.addChangeControl.controls['term'].value,
            name: this.addChangeControl.controls['name'].value,
            usage: this.addChangeControl.controls['usage'].value,
            comment: this.addChangeControl.controls['comments'].value,
            contributorName: this.addChangeControl.controls['contributorName'].value,
            contributorEmail: this.addChangeControl.controls['contributorEmail'].value,
        };
        this.termChangeService.add( target );
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
